import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { fetchAuthenticatedData } from "@/queries/shared";
import { CSPPIDMatchAction } from "@/models/mid";

const rootRoute = getRouteApi("__root__");

// Backend ActionStatus enum mirror.
// CREATED = 0, COMPLETED = 1, NOT_ALLOWED = 2, FAILED = -1
const ACTION_STATUS_COMPLETED = 1;
const ACTION_STATUS_NOT_ALLOWED = 2;
const ACTION_STATUS_FAILED = -1;

const POLLING_INTERVAL_MS = 2500;
const POLLING_TIMEOUT_MS = 60000;

type PollingPhase = "idle" | "polling" | "complete";

export type MidActionResultStatus = "completed" | "not_allowed" | "failed";

export type MidActionResult = {
  status: MidActionResultStatus;
  action: CSPPIDMatchAction | null;
};

export function useMidActionPolling(leaid: string, matchid: string) {
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();

  const [pollingActionId, setPollingActionId] = useState<number | null>(null);
  const [phase, setPhase] = useState<PollingPhase>("idle");
  const [result, setResult] = useState<MidActionResult | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);

  const isPolling = phase === "polling";

  const { data: actions } = useQuery<CSPPIDMatchAction[]>({
    queryKey: ["match-poll", leaid, matchid],
    queryFn: () =>
      fetchAuthenticatedData<CSPPIDMatchAction[]>(
        globalState,
        `/json/csppid_match/${leaid}/${matchid}`
      ),
    refetchInterval: isPolling ? POLLING_INTERVAL_MS : false,
    enabled: isPolling,
  });

  useEffect(() => {
    if (!isPolling || !pollingActionId || !actions) return;

    const action = actions.find((a) => a.Id === pollingActionId);
    if (!action) return;

    if (action.Status === ACTION_STATUS_COMPLETED) {
      setResult({ status: "completed", action });
      setPhase("complete");
    } else if (action.Status === ACTION_STATUS_NOT_ALLOWED) {
      setResult({ status: "not_allowed", action });
      setPhase("complete");
    } else if (action.Status === ACTION_STATUS_FAILED) {
      setResult({ status: "failed", action });
      setPhase("complete");
    }
  }, [actions, pollingActionId, isPolling]);

  // Stop polling after timeout to avoid infinite loops if the queue is stuck.
  useEffect(() => {
    if (!isPolling || !startTime) return;
    const timer = setTimeout(() => {
      setResult({ status: "failed", action: null });
      setPhase("complete");
    }, POLLING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPolling, startTime]);

  const startPolling = useCallback(
    (actionId: number) => {
      setResult(null);
      setPollingActionId(actionId);
      setStartTime(Date.now());
      setPhase("polling");
      // Invalidate so the next poll fetches fresh data immediately.
      queryClient.invalidateQueries({
        queryKey: ["match-poll", leaid, matchid],
      });
    },
    [queryClient, leaid, matchid]
  );

  const reset = useCallback(() => {
    setPollingActionId(null);
    setResult(null);
    setStartTime(null);
    setPhase("idle");
  }, []);

  return {
    startPolling,
    reset,
    phase,
    result,
    isPolling,
  };
}
